import { useAuth0 } from "@auth0/auth0-react";
import dayjs from "dayjs";
import React, { useState } from "react";

import BottomMenu from "../../components/BottomMenu";
import { DemoModeBanner } from "../../components/DemoModeBanner";
import { Header } from "../../components/Header";
import LeftMenu from "../../components/LeftMenu";
import LineButton from "../../components/LineButton";
import ModalConfirm from "../../components/ModalConfirm";
import ModalPopupMenu from "../../components/ModalPopupMenu";
import ModalPrivacyPolicyAndTermsService from "../../components/ModalPrivacyPolicyAndTermsService";
import ModalTermsConditions from "../../components/ModalTermsConditions";
import { ImpersonatedOrgaizationProvider } from "../../hooks/organizationImpersonation";
import { useMe } from "../../hooks/queries";
import { useTermsConditions } from "../../hooks/useTermsConditions";
import { AccountData, AccountType } from "../../models/AccountData";
import "./styles.scss";
import DataSvc from "../../services/dataSvc";

export interface NavigationContainerProps {
  children: any;
}

enum ForcedLogoutState {
  OrgUserWithoutOrg = "org_user_without_org",
  OrganizationExpired = "organization_expired",
}

export const checkForForcedLogout = (
  me: AccountData | undefined
): ForcedLogoutState | undefined => {
  if (!me) {
    return;
  }

  // invalid org users are forced to logout
  if (me.type === AccountType.ORG_ADMIN && !me.organization) {
    return ForcedLogoutState.OrgUserWithoutOrg;
  }

  if (me.organization?.plan_expiry_date) {
    // truncate the expiry date（UTC）based with JST
    const expday = dayjs(me?.organization?.plan_expiry_date)
      .add(-9, "hours")
      .add(1, "days")
      .minute(0)
      .second(0)
      .millisecond(0);
    const today = dayjs();
    if (expday.isBefore(today)) {
      return ForcedLogoutState.OrganizationExpired;
    }
  }
};

const NavigationContainer: React.FunctionComponent<
  NavigationContainerProps
> = ({ children }) => {
  const { logout } = useAuth0();
  const { data: me } = useMe();
  const {
    needsAcceptance,
    latestTerms,
    refetch: refetchTerms,
  } = useTermsConditions();

  const [popupMenuOpen, setPopupMenuOpen] = useState<boolean>(false);

  const [
    modalPrivacyPolicyAndTermsServiceType,
    setModalPrivacyPolicyAndTermsServiceType,
  ] = useState<string>("");
  const [shownModalConfirmLogout, setShownModalConfirmLogout] =
    useState<boolean>(false);

  const forcedLogoutState = checkForForcedLogout(me);

  const handleTermsAccepted = () => {
    refetchTerms();
  };

  return (
    <ImpersonatedOrgaizationProvider>
      <Header
        setModalPrivacyPolicyAndTermsServiceType={
          setModalPrivacyPolicyAndTermsServiceType
        }
        setShownModalConfirmLogout={setShownModalConfirmLogout}
      />
      <div className="page-content">
        <div className={`content-wrap flex`}>
          <LeftMenu />
          {children}
          <DemoModeBanner />
          <BottomMenu
            onOpenMenu={() => {
              setPopupMenuOpen(true);
            }}
          />
          <LineButton />
        </div>
      </div>
      {popupMenuOpen && (
        <ModalPopupMenu
          onClose={() => setPopupMenuOpen(false)}
          setModalPrivacyPolicyAndTermsServiceType={
            setModalPrivacyPolicyAndTermsServiceType
          }
          setShownModalConfirmLogout={setShownModalConfirmLogout}
        />
      )}

      {modalPrivacyPolicyAndTermsServiceType !== "" && (
        <ModalPrivacyPolicyAndTermsService
          type={modalPrivacyPolicyAndTermsServiceType}
          onClose={() => {
            setModalPrivacyPolicyAndTermsServiceType("");
          }}
        />
      )}

      {shownModalConfirmLogout && (
        <ModalConfirm
          title={"logout"}
          description={"are_you_sure_to_log_out"}
          cancelLabel={"cancel"}
          confirmLabel={"logout"}
          onClose={() => {
            setShownModalConfirmLogout(false);
          }}
          onConfirm={() => {
            logout({ returnTo: window.location.origin });
            setShownModalConfirmLogout(false);
          }}
        />
      )}

      {forcedLogoutState === ForcedLogoutState.OrgUserWithoutOrg && (
        <ModalConfirm
          title={"user_does_not_belong_to_any_org"}
          description={"user_needs_to_belong_to_an_org"}
          cancelLabel={""}
          confirmLabel={"ok"}
          onClose={() => {
            DataSvc.isExpired = false;
            logout({ returnTo: window.location.origin });
          }}
          onConfirm={() => {
            DataSvc.isExpired = false;
            logout({ returnTo: window.location.origin });
          }}
        />
      )}

      {forcedLogoutState === ForcedLogoutState.OrganizationExpired && (
        <ModalConfirm
          title={"organization_is_expired"}
          description={"unfortunately_your_organization_is_expired"}
          cancelLabel={""}
          confirmLabel={"ok"}
          onClose={() => {
            logout({ returnTo: window.location.origin });
          }}
          onConfirm={() => {
            logout({ returnTo: window.location.origin });
          }}
        />
      )}

      {needsAcceptance && latestTerms && (
        <ModalTermsConditions
          termsConditions={latestTerms}
          onAccepted={handleTermsAccepted}
        />
      )}
    </ImpersonatedOrgaizationProvider>
  );
};

export default NavigationContainer;
